import { NavLink, Outlet } from "react-router";
import { NAV, type NavIcon } from "../nav";

const ICONS: Record<NavIcon, string> = {
  events: "M4 7h16v10H4zM8 7V5M16 7V5M4 11h16",
  upcoming: "M4 9a2 2 0 002-2h12a2 2 0 002 2v6a2 2 0 00-2 2H6a2 2 0 00-2-2z",
  stats: "M5 20V11M12 20V5M19 20v-7",
  arenas: "M12 21s-6-5.5-6-11a6 6 0 0112 0c0 5.5-6 11-6 11z",
};

function Icon({ d }: { d: string }) {
  return (
    <svg aria-hidden width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d={d} />
    </svg>
  );
}

/**
 * Chrome around every signed-in page: the tabs across the top from `sm` up,
 * and as a fixed bottom bar on a phone, where the thumb already is.
 * `react-router`, never `react-router-dom`; eslint.config.js enforces it.
 */
export function Layout({ email, onSignOut }: { email: string | null; onSignOut: () => void }) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center gap-4 border-b border-line/70 px-4 py-2.5">
        <span className="font-display text-xl font-bold tracking-[0.06em] uppercase">
          Sports<span className="text-gold">·</span>Tracker
        </span>
        <nav aria-label="Pages" className="hidden gap-1 sm:flex">
          {NAV.map((e) => (
            <NavLink
              key={e.to}
              to={e.to}
              end={e.to === "/"}
              className={({ isActive }) =>
                `rounded-md px-3 py-1.5 text-[15px] ${isActive ? "bg-raised text-neutral-100" : "text-muted hover:text-neutral-200"}`
              }
            >
              {e.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3 text-sm text-muted">
          <NavLink
            to="/people"
            aria-label="People"
            className={({ isActive }) =>
              `grid h-11 w-11 place-items-center rounded-md ${isActive ? "bg-raised text-neutral-100" : "hover:text-neutral-200"}`
            }
          >
            <svg aria-hidden width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="9" cy="8" r="3.5" />
              <path d="M2.5 20c1-3.5 3.8-5 6.5-5s5.5 1.5 6.5 5" />
              <circle cx="17" cy="9" r="2.5" />
              <path d="M16 14.5c2.5 0 4.6 1.3 5.5 4" />
            </svg>
          </NavLink>
          <span className="hidden font-mono sm:inline">{email ?? "—"}</span>
          <button onClick={onSignOut} className="underline hover:text-neutral-200">
            Sign out
          </button>
        </div>
      </header>

      <nav
        aria-label="Tabs"
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-line/70 bg-ground/95 pb-[env(safe-area-inset-bottom)] backdrop-blur sm:hidden"
      >
        {NAV.map((e) => (
          <NavLink
            key={e.to}
            to={e.to}
            end={e.to === "/"}
            className={({ isActive }) =>
              `grid min-h-14 place-items-center gap-0.5 pt-1.5 text-[11.5px] ${isActive ? "text-neutral-100" : "text-faint"}`
            }
          >
            <Icon d={ICONS[e.icon]} />
            {e.label}
          </NavLink>
        ))}
      </nav>

      {/* pb-28 clears the fixed bottom bar, which is out of flow. */}
      <main className="mx-auto w-full max-w-5xl flex-1 p-4 pb-28 sm:px-6 sm:pb-8">
        <Outlet />
      </main>
    </div>
  );
}
